import { Lock } from 'lucide-react';
import { useState } from 'react';

import { useUrlState } from '@/hooks/useUrlState';
import { Badge } from '@/components/ui/badge';
import { Markdown } from '@/components/ui/markdown';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { formatMoney, canEditCommission, canSeeMoney } from '@/lib/money';
import { useSession } from '@/stores/session';
import { AttachmentsPanel } from '@/features/documents';
import { CommissionsPanel } from '@/features/finance/commissions';
import { EmployeeCostPanel } from '@/features/finance/recurring-expenses';
import {
  pdpStatusLabel,
  pdpStatusVariant,
} from '@/features/hrm/development/constants';
import { useEmployeeSkills, usePdps, useSkills } from '@/features/hrm/development/queries';
import {
  onboardingKindLabel,
  onboardingStatusLabel,
  onboardingStatusVariant,
} from '@/features/hrm/onboarding';
import { useOnboardings } from '@/features/hrm/onboarding/queries';
import { reviewStatusLabel, reviewStatusVariant } from '@/features/hrm/reviews/constants';
import { useReviews } from '@/features/hrm/reviews/queries';
import { LEAVE_STATUS_LABEL, LEAVE_TYPE_LABEL } from '@/features/hrm/time-off/constants';
import { useTimeOff } from '@/features/hrm/time-off/queries';
import { useEmployee, useEmployeePayslips, useSalaryRecords, useUpdateEmployee } from '../queries';
import { AddSalaryRecordForm } from './AddSalaryRecordForm';
import { BankAccountsPanel } from './BankAccountsPanel';

const ALL_TABS = [
  'Salary',
  'Payslips',
  'Bank accounts',
  'Time off',
  'Reviews',
  'Development',
  'Onboarding',
  'Skills',
  'Documents',
  'Commissions',
  'Expenses',
] as const;
type Tab = (typeof ALL_TABS)[number];

// Roles allowed to manage (pay out / void) commissions — mirrors the backend
// COMMISSION_MANAGE_ROLES. Sales Manager can see but not manage.
const COMMISSION_MANAGE_ROLES = new Set(['founder', 'super_admin', 'accountant']);

// Bank accounts — HR / Founder / Super Admin / Accountant manage them (mirrors
// the backend BANK_ACCOUNT_MANAGE_ROLES; Accountant runs payroll).
const BANK_ACCOUNT_ROLES = new Set(['hr', 'founder', 'super_admin', 'accountant']);

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

/** HR-side mirror of the employee's cabinet data, on one page. Salary stays
 * masked server-side for roles outside SALARY_VISIBLE_ROLES (HR included). */
export function EmployeeDetailTabs({
  employeeId,
  isSales,
}: {
  employeeId: number;
  isSales: boolean;
}) {
  const [tab, setTab] = useUrlState<Tab>('empTab', 'Salary', {
    push: true,
    deserialize: (r) => ((ALL_TABS as readonly string[]).includes(r) ? (r as Tab) : 'Salary'),
  });
  const role = useSession((s) => s.role);
  // Commissions are comp-sensitive (C7): only money-visible roles see the
  // section — and only for salespeople, since commission is a sales concept
  // (ADR-046). Only manage roles can pay out / void.
  const showCommissions = canSeeMoney(role) && isSales;
  const canManageCommissions = role != null && COMMISSION_MANAGE_ROLES.has(role);
  // Per-employee cost (recurring expenses) is finance-only — mirrors the backend
  // RECURRING_EXPENSE_VIEW_ROLES (Founder/Super Admin/Accountant; HR excluded).
  const showCosts = role != null && COMMISSION_MANAGE_ROLES.has(role);
  // Payslips (actually-paid salary) come from the payroll endpoint, gated to
  // SALARY_VISIBLE_ROLES (Founder/Super Admin/Accountant; HR 403s) — same set.
  const showPayslips = showCosts;
  const showBankAccounts = role != null && BANK_ACCOUNT_ROLES.has(role);
  const tabs = ALL_TABS.filter((t) => {
    if (t === 'Commissions') return showCommissions;
    if (t === 'Expenses') return showCosts;
    if (t === 'Payslips') return showPayslips;
    if (t === 'Bank accounts') return showBankAccounts;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="border-b">
        <nav className="-mb-px flex flex-wrap gap-2" aria-label="Employee sections">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                tab === t
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'Salary' ? (
        <SalaryPanel
          employeeId={employeeId}
          // Add-record is Founder/Super Admin only: HR can technically write
          // server-side but is masked from amounts, so a form would be blind.
          canManage={role === 'founder' || role === 'super_admin'}
          // Commission default only matters for salespeople (ADR-046).
          isSales={isSales}
        />
      ) : null}
      {tab === 'Time off' ? <TimeOffPanel employeeId={employeeId} /> : null}
      {tab === 'Reviews' ? <ReviewsPanel employeeId={employeeId} /> : null}
      {tab === 'Development' ? <PdpPanel employeeId={employeeId} /> : null}
      {tab === 'Onboarding' ? <OnboardingPanel employeeId={employeeId} /> : null}
      {tab === 'Skills' ? <SkillsPanel employeeId={employeeId} /> : null}
      {tab === 'Documents' ? (
        <AttachmentsPanel modelLabel="hrm.employee" objectId={employeeId} />
      ) : null}
      {tab === 'Commissions' && showCommissions ? (
        <CommissionsPanel employeeId={employeeId} canManage={canManageCommissions} />
      ) : null}
      {tab === 'Expenses' && showCosts ? <EmployeeCostPanel employeeId={employeeId} /> : null}
      {tab === 'Payslips' && showPayslips ? <PayslipsPanel employeeId={employeeId} /> : null}
      {tab === 'Bank accounts' && showBankAccounts ? (
        <BankAccountsPanel employeeId={employeeId} canManage={showBankAccounts} />
      ) : null}
    </div>
  );
}

/** Actually-paid salary — payslips from finalized/draft payroll runs (ADR-048).
 *  Distinct from "Salary records" (the agreed compensation that drives them). */
function PayslipsPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = useEmployeePayslips(employeeId);
  const rows = data?.results ?? [];
  return (
    <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No payslips paid yet.">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Deductions</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="hidden text-right md:table-cell">Net (USD)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.public_id}>
                <TableCell>
                  {fmtDate(p.period_start)} – {fmtDate(p.period_end)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(p.gross, p.currency) ?? (
                    <span className="text-muted-foreground inline-flex items-center gap-1">
                      <Lock className="size-3" /> Restricted
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums sm:table-cell">
                  {formatMoney(p.deductions, p.currency) ?? '—'}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatMoney(p.net, p.currency) ?? '—'}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums md:table-cell">
                  {formatMoney(p.base_net, 'USD') ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
}

function Panel({
  isPending,
  isEmpty,
  emptyText,
  children,
}: {
  isPending: boolean;
  isEmpty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  // Keep a steady min-height across the loading → content/empty swap so the
  // first open of a tab doesn't change page height (which would otherwise
  // toggle the window scrollbar and nudge the layout).
  if (isPending) return <Skeleton className="min-h-48 w-full" />;
  if (isEmpty)
    return (
      <div className="text-muted-foreground flex min-h-48 items-center justify-center py-8 text-center text-sm">
        {emptyText}
      </div>
    );
  return <div className="min-h-48">{children}</div>;
}

function SalaryPanel({
  employeeId,
  canManage,
  isSales,
}: {
  employeeId: number;
  canManage: boolean;
  isSales: boolean;
}) {
  const { data, isPending } = useSalaryRecords(employeeId);
  // Newest-effective first; the latest entry is the operative one (drives payroll
  // prefill server-side via `latest_salary_record`), so it's marked "Current".
  const rows = [...(data?.results ?? [])].sort((a, b) =>
    b.effective_date.localeCompare(a.effective_date),
  );
  const currentId = rows[0]?.id ?? null;
  const current = rows[0];
  const currentAmount = current ? formatMoney(current.amount, current.currency) : null;

  return (
    <div className="space-y-4">
      {isSales ? <CommissionDefaultCard employeeId={employeeId} /> : null}
      <Card>
        <CardHeader>
          <CardTitle>Records</CardTitle>
          <CardDescription>
            {current && currentAmount
              ? `Current: ${currentAmount} (effective ${fmtDate(current.effective_date)}).`
              : 'Effective-dated compensation records — the latest entry drives payroll prefill.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No salary records.">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Effective</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="hidden text-right sm:table-cell">Base (USD)</TableHead>
                    <TableHead className="hidden md:table-cell">Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const amount = formatMoney(r.amount, r.currency);
                    const isCurrent = r.id === currentId;
                    return (
                      <TableRow key={r.public_id} className={cn(isCurrent && 'bg-accent/40')}>
                        <TableCell>
                          <span className="inline-flex items-center gap-2">
                            {fmtDate(r.effective_date)}
                            {isCurrent ? <Badge>Current</Badge> : null}
                          </span>
                        </TableCell>
                        <TableCell
                          className={cn('text-right tabular-nums', isCurrent && 'font-semibold')}
                        >
                          {amount ?? (
                            <span className="text-muted-foreground inline-flex items-center gap-1">
                              <Lock className="size-3" /> Restricted
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="hidden text-right tabular-nums sm:table-cell">
                          {formatMoney(r.base_amount, 'USD') ?? '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground hidden md:table-cell">
                          {r.note || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Panel>
        </CardContent>
      </Card>

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>Add salary record</CardTitle>
            <CardDescription>
              Effective-dated — adding a record never edits history. New payslips prefill from
              the latest entry.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AddSalaryRecordForm employeeId={employeeId} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/** The per-person default commission rate that seeds new deals this employee
 *  owns (ADR-046). Editable by compensation roles; masked ("Restricted") for
 *  others — the backend drops the field from their write payload regardless. */
function CommissionDefaultCard({ employeeId }: { employeeId: number }) {
  const role = useSession((s) => s.role);
  const canEdit = canEditCommission(role);
  const { data: employee, isPending } = useEmployee(employeeId);
  const update = useUpdateEmployee(employeeId);
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);

  const current = employee?.default_commission_pct ?? null;
  const eligible = employee?.commission_eligible ?? false;

  function toggleEligible(next: boolean) {
    update.mutate({ commission_eligible: next });
  }

  function startEdit() {
    setValue(current ?? '');
    setEditing(true);
  }

  function save() {
    const trimmed = value.trim();
    update.mutate(
      { default_commission_pct: trimmed === '' ? null : trimmed },
      { onSuccess: () => setEditing(false) },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Commission</CardTitle>
        <CardDescription>
          Whether this person earns sales commission, and the default rate seeded onto new deals
          they own. Existing deals keep their own rate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {isPending ? (
          <Skeleton className="h-10 w-40" />
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm">
              <p className="font-medium">Commission eligible</p>
              <p className="text-muted-foreground">
                Off by default — deals only accrue commission when this is on.
              </p>
            </div>
            <Switch
              checked={eligible}
              disabled={!canEdit || update.isPending}
              onCheckedChange={toggleEligible}
              aria-label="Commission eligible"
            />
          </div>
        )}
        {isPending || !eligible ? null : (
          <div>
            <p className="mb-1.5 text-sm font-medium">Default rate</p>
            {editing ? (
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="e.g. 10"
                  className="w-32"
                />
                <Button size="sm" onClick={save} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold tabular-nums">
                  {current != null ? (
                    `${Number(current)}%`
                  ) : canEdit ? (
                    <span className="text-muted-foreground text-base font-normal">Not set</span>
                  ) : (
                    <span className="text-muted-foreground inline-flex items-center gap-1 text-base font-normal">
                      <Lock className="size-3" /> Restricted
                    </span>
                  )}
                </span>
                {canEdit ? (
                  <Button size="sm" variant="outline" onClick={startEdit}>
                    {current != null ? 'Edit' : 'Set rate'}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TimeOffPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = useTimeOff({ employee: employeeId });
  const rows = data?.results ?? [];
  return (
    <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No time-off requests.">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead className="text-right">Days</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{LEAVE_TYPE_LABEL[r.type]}</TableCell>
                <TableCell>
                  {fmtDate(r.start_date)} – {fmtDate(r.end_date)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.days}</TableCell>
                <TableCell>{LEAVE_STATUS_LABEL[r.status]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
}

function ReviewsPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = useReviews({ employee: employeeId });
  const rows = data?.results ?? [];
  return (
    <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No reviews.">
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="rounded-md border p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{r.period || 'Review'}</span>
              <Badge variant={reviewStatusVariant(r.status)}>{reviewStatusLabel(r.status)}</Badge>
            </div>
            {r.summary ? <Markdown className="mt-1">{r.summary}</Markdown> : null}
          </div>
        ))}
      </div>
    </Panel>
  );
}

function PdpPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = usePdps({ employee: employeeId });
  const rows = data?.results ?? [];
  return (
    <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No development plans.">
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} className="rounded-md border p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{p.title}</span>
              <Badge variant={pdpStatusVariant(p.status)}>{pdpStatusLabel(p.status)}</Badge>
            </div>
            {p.summary ? <Markdown className="mt-1">{p.summary}</Markdown> : null}
          </div>
        ))}
      </div>
    </Panel>
  );
}

function OnboardingPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = useOnboardings({ employee: employeeId });
  const rows = data?.results ?? [];
  return (
    <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No onboarding checklists.">
      <div className="space-y-3">
        {rows.map((ob) => {
          const done = ob.items.filter((i) => i.is_done).length;
          return (
            <div key={ob.id} className="rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium">{onboardingKindLabel(ob.kind)}</span>
                <span className="flex items-center gap-2">
                  <span className="text-muted-foreground">
                    {done}/{ob.items.length} done
                  </span>
                  <Badge variant={onboardingStatusVariant(ob.status)}>
                    {onboardingStatusLabel(ob.status)}
                  </Badge>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function SkillsPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = useEmployeeSkills({ employee: employeeId });
  const catalog = useSkills();
  const rows = data?.results ?? [];
  const nameById = new Map((catalog.data?.results ?? []).map((s) => [s.id, s.name]));
  return (
    <Panel isPending={isPending} isEmpty={rows.length === 0} emptyText="No skills recorded.">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Skill</TableHead>
              <TableHead className="text-right">Level</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{nameById.get(s.skill) ?? `#${s.skill}`}</TableCell>
                <TableCell className="text-right tabular-nums">{s.level}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
}
